<template>
  <div
    v-show="isEngineer || isTenant"
    class="my-tenant-projects dashboard"
  >
    <h6
      v-if="isTenant && loading"
      class="font-dark-gray"
    >
      <i class="rotation fa fa-refresh" />
      {{ $t('loading') }}
    </h6>
    <div v-show="isEngineer || projectData && projectData.length > 0">
      <div :class="['tms-dashboard__panel-header row', !isEngineer ? 'no-margin-bottom' : '']">
        <div class="tms-dashboard__panel-container-without-button">
          <div class="tms-dashboard__panel-title-box">
            <div :class="['dashboard__title', isEngineer ? 'no-margin-bottom' : '']">
              {{ $t('tender.general.fitoutAndReinstatement') }}
              <i
                v-if="isEngineer"
                id="dashboard_refresh"
                class="fa fa-refresh dark flat icon_refresh"
                @click="loadTenantProject()"
              />
            </div>
            <div
              v-if="isEngineer"
              class="tms-dashboard__panel-view-all"
            >
              <router-link to="/tenant-projects">
                {{ $t('viewAll') }} >
              </router-link>
            </div>
          </div>
          <div
            v-if="isEngineer"
            class="tms-dashboard__panel-toolbar"
          >
            <div class="handover-takeback__counter--duration tms-dashboard__panel-toolbar-right">
              <span>{{ $t('showLabel') }}:</span>
              <HKLSelect
                :options="timeHklOptions"
                :showSearch="false"
                :value="selectedTimeValue"
                @change="selectStatusDate"
              />
            </div>
          </div>
        </div>
        <HKLButton
          v-if="isEngineer"
          color="darkblue"
          action="resetFilter"
          class="tms-dashboard__panel-reset-btn"
          @click="resetFilter"
        />
      </div>
      <div class="dashboard__body">
        <div class="dashboard__table">
          <table class="sort_table">
            <thead class="table-header-title">
              <tr class="dashboard__table--header">
                <th
                  v-for="header in tableHeaders"
                  :key="header.name"
                  :class="header.name"
                  @click="sortBy(header.name)"
                >
                  <span
                    class="emphasis"
                  >
                    {{ header.title }}
                    <i
                      v-if="orderbyAttr === header.name"
                      :class="[reverse ? 'triangle-down' : 'triangle-up']"
                    />
                  </span>
                </th>
              </tr>
              <tr
                v-if="isHKlandUser"
                class="table-filter"
              >
                <th
                  v-for="header in tableHeaders"
                  :key="header.name"
                  :class="header.name"
                >
                  <div
                    class="table-filter-container"
                  >
                    <component
                      :is="header.filter"
                      v-bind="header.filterAttrs || {}"
                      v-if="header.filter"
                      v-model="filterObject[header.name]"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody class="table-content">
              <tr
                v-for="(item, index) in filteredItems"
                :key="index"
                class="dashboard__table--body"
              >
                <td
                  v-for="header in tableHeaders"
                  :key="header.name"
                >
                  <a
                    v-if="header.name === 'projectLocation'"
                    class="link_text project-location"
                    @click="jumpAfterSettingTabIndex({ name: tenantProjectRouteName, params: { id: item.id }})"
                  >
                    {{ item[header.name] || emptyValue }}
                  </a>
                  <template v-else-if="header.name === 'batchStatus'">
                    <a
                      v-if="(!item[header.name] && !item.postFitoutBatchStatus && !item.greenFitOutBatchStatus)"
                      class="link_text"
                      @click="navigateBatchStatus(item, '1')"
                    >
                      {{ `${emptyValue}/${emptyValue}` }}
                    </a>
                    <div>
                      <a
                        v-if="item[header.name]"
                        class="link_text"
                        @click="navigateBatchStatus(item, '1')"
                      >
                        {{ item[header.name] }}
                      </a>
                    </div>
                    <div>
                      <a
                        v-if="item.postFitoutBatchStatus"
                        class="link_text"
                        @click="navigateBatchStatus(item, '2')"
                      >
                        {{ item.postFitoutBatchStatus }}
                      </a>
                    </div>
                    <div>
                      <a
                        v-if="item.greenFitOutBatchStatus"
                        class="link_text"
                        @click="navigateBatchStatus(item, '3')"
                      >
                        {{ item.greenFitOutBatchStatus }}
                      </a>
                    </div>
                    <div
                      v-if="item.isNewDoc && isEngineer"
                      class="new-doc"
                    >
                      {{ $t('tenantProjects.docs') }}
                    </div>
                  </template>
                  <div
                    v-else-if="header.name === 'tradeStatus' && isEngineer"
                  >
                    <div class="pin-placeholder">
                      <div
                        v-if="item.isShowDot"
                        class="pin__dot-green"
                      />
                      <div
                        v-if="item.isShowPin"
                        class="pin__bubble-orange"
                      >
                        <i
                          class="fa fa-thumb-tack"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    v-else-if="header.name === 'tradeName' && isEngineer"
                    class="trade-name__display"
                  >
                    <span>{{ item[header.name] || emptyValue }}</span>
                  </div>
                  <span v-else-if="header.name === 'date'">{{ dateUtil.formatDate(item[header.name]) }}</span>
                  <span v-else-if="header.name === 'projectType'">{{ (item[header.name] || emptyValue) | customTProjectTypeLabel }}</span>
                  <span v-else-if="header.name === 'status'">{{ (item[header.name] || emptyValue) | customStatusLabel }}</span>
                  <span v-else>{{ item[header.name] || emptyValue }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import moment from 'moment'
import { computed, getCurrentInstance, unref } from 'vue-demi'
import { mapActions, mapGetters, mapState } from 'vuex'
import { getFloorIndex } from '@/api/buildings/request'
import * as tenantProjectApi from '@/api/tenantProjects/request'
import HKLButton from '@/controls/Button/HKLButton'
import HKLDateRange from '@/controls/Date/HKLDateRange'
import HKLInput from '@/controls/Input/HKLInput'
import HKLMultiSelect, { ALL_VALUE } from '@/controls/Select/HKLMultiSelect'
import HKLSelect from '@/controls/Select/HKLSelect'
import filters from '@/filters/filters'
import { showDocForGreenFitout, showDocForSubmission } from '@/modules/tenantProjects/utils'
import { useStoreGetters } from '@/store'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import { DesignBatchStatus, tenantProjectStatus, tenantProjectTypes } from '@/utils/locale'
import navigation from '@/utils/navigation'
import useRegionRef from '@/utils/useRegionRef'
import { orderItemByGroup } from '@/utils/sortingHelper'
import { PROJECT_STATUS_FOR_WP, TENANT_PROJECT_TYPES, timeOptions } from './consts'
import { getBatchStatus, getGreenFitOutStatus, getPostFitoutStatus } from './fitout.service'
import { tenantProjectIsVisibleOnEngineerDashboard, tenantProjectRecordHasFitoutDashboardPin } from './hook'

const filterObject = useRegionRef({ }, { logoutReset: true })

export default {
  components: {
    HKLButton,
    HKLSelect,
  },
  props: {
    preload: {
      type: Boolean,
      default: false,
    },
    preloadedTenantProjects: {
      type: Array,
      default () { return [] },
    },
  },
  setup () {
    const { proxy } = getCurrentInstance()
    const { isHKlandUser } = useStoreGetters('user', ['isHKlandUser'])
    const selectOptions = computed(() => ({
      projectType: [
        {
          name: i18n.t('all'),
          value: ALL_VALUE,
        },
        {
          name: filters.customTProjectTypeLabel('fitout'),
          value: tenantProjectTypes.Fitout,
        },
        {
          name: filters.customTProjectTypeLabel('reinstatement'),
          value: tenantProjectTypes.Reinstatement,
        },
      ],
      status: [
        {
          name: filters.customStatusLabel('draft'),
          value: tenantProjectStatus.DRAFT,
        },
        {
          name: filters.customStatusLabel('submitted'),
          value: tenantProjectStatus.SUBMITTED,
        },
        {
          name: filters.customStatusLabel('inprogress'),
          value: tenantProjectStatus.IN_PROGRESS,
        },
      ],
    }))
    const tableHeaders = computed(() => {
      const headers = [
        {
          name: 'projectLocation',
          title: i18n.t('settings.location'),
          filter: HKLInput,
        },
        {
          name: 'tradeName',
          title: i18n.t('tenancyProject.tradeName'),
          filter: HKLInput,
        },
        {
          name: 'projectType',
          title: i18n.t('tenancyProject.type'),
          filter: HKLMultiSelect,
          filterAttrs: {
            includeAll: false,
            options: unref(selectOptions).projectType,
          },
        },
        {
          name: 'batchStatus',
          title: i18n.t('tenantProjects.submission.batchStatus'),
          filter: HKLInput,
        },
        {
          name: 'floor',
          title: i18n.t('tenancyProject.floor'),
          filter: HKLInput,
        },
        {
          name: 'status',
          title: i18n.t('tenancyProject.status'),
          filter: HKLMultiSelect,
          filterAttrs: {
            includeAll: true,
            options: unref(selectOptions).status,
          },
        },
        {
          name: 'date',
          title: i18n.t('statusDate'),
          filter: HKLDateRange,
          filterAttrs: {
            canPutTotop: false,
            placeholders: [i18n.t('dateFrom'), i18n.t('dateTo')],
            displayRange: [unref(filterObject).date && unref(filterObject).date[1] ? { to: unref(filterObject).date[1] } : undefined],
          },
        },
      ]
      if (unref(isHKlandUser)) {
        headers.splice(headers.findIndex((h) => h.name === 'tradeName'), 0, {
          name: 'tradeStatus',
          title: '',
        })
      }
      return headers
    })
    const { buildingIdContentMaps } = useStoreGetters('buildings', ['buildingIdContentMaps'])
    const validItems = computed(() => {
      return proxy.projectData.filter((rec) => tenantProjectIsVisibleOnEngineerDashboard(rec, proxy.currentUser))
    })
    const parsedItems = computed(() => validItems.value
      .map((rec) => {
        const projectLocation = !lodash.isEmpty(rec.projectLocation)
          ? rec.projectLocation.join(', ')
          : proxy.$t('emptyValue')
        const buildingId = rec.tenancy.buildingId
        const buildingCode = lodash.get(unref(buildingIdContentMaps), `${buildingId}.code`)
        const sortedFloors = lodash.orderBy(rec.floors, floor => getFloorIndex(buildingCode, floor))
        const firstFloor = lodash.get(sortedFloors, 0, Number.MIN_SAFE_INTEGER)

        const [ batchStatus, batchStatusIndex ] = getBatchStatus(rec, { tenantView: proxy.isTenant })
        const [ postFitoutBatchStatus, postFitoutBatchStatusIndex ] = getPostFitoutStatus(rec)
        let isShowPin = false
        let isNewDoc = false
        const hasGreenFitOut = !lodash.isEmpty(rec.greenFitoutSubmissions)
        const refGreenFitOut = hasGreenFitOut ? rec.greenFitoutSubmissions[0] : {}
        if (unref(isHKlandUser)) {
          isShowPin = tenantProjectRecordHasFitoutDashboardPin(rec)
          isNewDoc = (showDocForSubmission(rec) || showDocForGreenFitout(refGreenFitOut))
        }
        return {
          id: rec.id,
          status: rec.status,
          date: rec.statusUpdatedAt || proxy.$t('emptyValue'),
          projectType: rec.type,
          projectLocation: `(${buildingCode}) ${projectLocation}` + (rec.description ? `\n${rec.description}` : ''),
          tradeName: rec.tenancy.tradeName,
          projectName: rec.projectName,
          floor: sortedFloors.length ? sortedFloors.join(', ') : proxy.$t('emptyValue'),
          floorIndex: getFloorIndex(buildingCode, firstFloor),
          isNewDoc,
          isShowPin,
          isShowDot: unref(isHKlandUser) ? proxy.getIsShowDot(rec) : false,
          batchStatus,
          batchStatusIndex,
          postFitoutBatchStatus,
          postFitoutBatchStatusIndex,
          greenFitOutBatchStatus: getGreenFitOutStatus(rec),
          greenFitOutBatchStatusIndex: hasGreenFitOut ? 0 : Number.MIN_SAFE_INTEGER,
        }
      }),
    )
    const sortedItems = computed(() => {
      const { orderbyAttr } = proxy
      const list = unref(parsedItems)
      if (orderbyAttr) {
        const attrs = [orderbyAttr]
        if (orderbyAttr === 'batchStatus') {
          attrs.push('postFitoutBatchStatus', 'greenFitOutBatchStatus')
        }
        const iteratee = []
        for (const attr of attrs) {
          const attrIndex = `${attr}Index`
          const hasIndex = lodash.has(list, `0.${attrIndex}`)
          iteratee.push(hasIndex ? attrIndex : attr)
        }
        const orders = Array(iteratee.length).fill(proxy.reverse ? 'desc' : 'asc')
        return lodash.orderBy(list, iteratee, orders)
      } else {
        const order = ['isShowPin', 'isNewDoc', 'isShowDot']
        return lodash.orderBy(list, [(item) => orderItemByGroup(item, order), 'date'], ['asc', 'desc'])
      }
    })
    const filteredItems = computed(() => {
      let list = unref(sortedItems)
      const obj = unref(filterObject)
      for (const key of ['projectLocation', 'tradeName', 'floor']) {
        const value = (obj[key] || '').trim().toLowerCase()
        if (!value) continue
        list = list.filter(rec => rec[key].toLowerCase().includes(value))
      }
      for (const key of ['projectType', 'status']) {
        let values = obj[key] || []
        if (values.includes(ALL_VALUE)) values = unref(selectOptions)[key].map(item => item.value)
        if (values.length === 0) continue
        list = list.filter(rec => values.includes(rec[key]))
      }
      if (obj.batchStatus) {
        const value = (obj.batchStatus || '').trim().toLowerCase()
        if (value) {
          const filterColumnNames = ['batchStatus', 'postFitoutBatchStatus', 'greenFitOutBatchStatus']
          list = list.filter((rec) => {
            const content = Object.values(lodash.pick(rec, filterColumnNames)).join('').toLowerCase()
            return content.includes(value)
          })
        }
      }
      if (obj.date && obj.date.map(d => !!d).includes(true)) {
        const [startDate, endDate] = obj.date.map(d => d && moment(d))
        if (startDate) {
          list = list.filter((rec) => {
            const current = moment(rec.date)
            return current.isSameOrAfter(startDate)
          })
        }
        if (endDate) {
          list = list.filter((rec) => {
            const current = moment(rec.date)
            return current.isSameOrBefore(endDate)
          })
        }
      }
      return list
    })
    return {
      isHKlandUser,
      filterObject,
      filteredItems,
      tableHeaders,
      buildingIdContentMaps,
    }
  },
  data () {
    return {
      canCommentDesign: aclHelper.getAccess(
        tmsPermsTypes.project.commentDesign,
      ),
      canCommentTech: aclHelper.getAccess(tmsPermsTypes.project.commentTech),
      dateUtil,
      dateFormat: 'YYYY-MM-DD',
      canSend: aclHelper.getAccess(tmsPermsTypes.project.send),
      emptyValue: this.$t('emptyValue'),
      loading: true,
      orderbyAttr: null,
      projectData: [],
      reverse: true,
      submissionsTabIndex: 1,
      timeOptions,
    }
  },
  computed: {
    ...mapGetters('hotb', ['myTenantProjectsFilterDateIndex']),
    ...mapGetters('user', ['currentUser', 'isTenant', 'isEngineer']),
    ...mapGetters('regions', ['enableGreenFitOutFeature']),
    ...mapGetters('dashboard', ['isTenantProjectsListStale']),
    ...mapState('dashboard', { getTenantProjects: 'tenantProjects' }),
    tenantProjectRouteName () {
      return this.isTenant ? 'tenantProjectDetail' : 'tenantProject'
    },
    timeHklOptions () {
      return this.timeOptions.map(option => ({
        name: this.$options.filters.customTimeLabel(option),
        value: option.value,
      }))
    },
    selectedTimeValue () {
      return (this.timeOptions[this.myTenantProjectsFilterDateIndex] || {}).value
    },
    statusChangedEndDay () {
      return this.statusDayFormat(moment().add(1, 'days'))
    },
    statusChangedStartDay () {
      const value = this.isTenant ? this.timeOptions[this.timeOptions.length - 1].value : this.timeOptions[this.myTenantProjectsFilterDateIndex].value
      const ref = this.statusDayFormat(this.statusChangedEndDay - value)
      return value === 0 ? null : ref
    },
    hasInsuranceAccess () {
      return aclHelper.getAccess(tmsPermsTypes.project.updateInsurance)
    },
  },
  async mounted () {
    if (this.preload) {
      this.projectData = this.preloadedTenantProjects
    } else {
      await this.hydrateOrFetchTenantProjects()
    }
    this.loading = false
  },
  activated () {
    if (!this.preload) {
      this.hydrateOrFetchTenantProjects()
    }
  },
  methods: {
    ...mapActions('hotb', ['setMyTenantProjectsFilterDateIndex']),
    ...mapActions('dashboard', ['loadTPForEngineer', 'setSortedAttributes']),
    getIsShowDot (rec) {
      if (!this.hasInsuranceAccess) return false
      const insurance = rec.insurance || {}
      let tenantConfirmedAt = new Date(insurance.tenantConfirmedAt)
      let hklConfirmedAt = new Date(insurance.updatedAt)
      let latestDocCreatedAt = new Date(rec.lastestInsurDocCreatedAt)
      if (!rec.lastestInsurDocCreatedAt || latestDocCreatedAt < hklConfirmedAt) {
        return false
      } else {
        return !!insurance.tenantConfirmedAt && (tenantConfirmedAt > hklConfirmedAt)
      }
    },
    getSubmissionSubmittedDesignComment (submission) {
      return lodash.get(submission, 'submittedDesignComment.comment')
    },
    isSubmissionCPDisapprove (submission) {
      return this.getSubmissionSubmittedDesignComment(submission) === DesignBatchStatus.DISAPPROVE
    },
    isSubmissionCPNoFollowUp (submission) {
      return this.getSubmissionSubmittedDesignComment(submission) === DesignBatchStatus.PROCEED_NO_FOLLOW_UP
    },
    statusDayFormat (date) {
      return new Date(date)
    },
    async selectStatusDate (value) {
      this.setMyTenantProjectsFilterDateIndex({
        index: this.timeOptions.findIndex(rec => rec.value === value),
      })
      await this.loadTenantProject()
    },
    sortBy (attributePath) {
      if (attributePath === 'tradeStatus') return
      if (this.orderbyAttr === attributePath) {
        this.reverse = !this.reverse
      }
      this.orderbyAttr = attributePath
      this.setSortedToStore()
    },
    jumpAfterSettingTabIndex (params) {
      navigation.toPage(params.name, params.params, params.query)
    },
    navigateBatchStatus (item, submissionActiveKey) {
      const safeSubmissionActiveKey = submissionActiveKey === '3' && !this.enableGreenFitOutFeature
        ? '1'
        : submissionActiveKey
      this.jumpAfterSettingTabIndex({
        name: this.tenantProjectRouteName,
        params: { id: item.id },
        query: {
          submissionActiveKey: safeSubmissionActiveKey,
          tabIndex: this.submissionsTabIndex,
        },
      })
    },
    getParamsForLoadTenantProject () {
      const undisplayStatus = [PROJECT_STATUS_FOR_WP.WITHDRAWN, PROJECT_STATUS_FOR_WP.CANCELLED, PROJECT_STATUS_FOR_WP.COMPLETED]
      let project = {'^status': undisplayStatus.join(',')}
      project = this.canCommentDesign && !this.canCommentTech ? { ...project, '^type': TENANT_PROJECT_TYPES.Reinstatement } : project
      let params = {
        statusChangedStartDay: this.statusChangedStartDay,
        statusChangedEndDay: this.statusChangedEndDay,
        attrSpecified: true,
        project,
      }
      return params
    },
    async loadTenantProject () {
      const params = this.getParamsForLoadTenantProject()
      if (this.isTenant) {
        this.projectData = await tenantProjectApi.list(params, true)
      } else {
        this.projectData = await this.loadTPForEngineer(params)
      }
      this.loading = false
    },
    async hydrateOrFetchTenantProjects () {
      if (!this.isEngineer) {
        await this.loadTenantProject()
        return
      }
      const tenantProjectInStore = lodash.get(this.getTenantProjects, 'contentList', [])
      if (tenantProjectInStore.length && !this.isTenantProjectsListStale) {
        this.projectData = tenantProjectInStore
      } else {
        await this.loadTenantProject()
      }

      const sortedAttributes = lodash.get(this.getTenantProjects, 'sortedAttributes', {})
      const { sortingAttr, reverse } = sortedAttributes
      this.orderbyAttr = sortingAttr || null
      this.reverse = typeof reverse === 'boolean' ? reverse : true
    },

    resetFilter () {
      this.orderbyAttr = null
      this.reverse = true
      filterObject.value = { }
      this.setSortedToStore()
    },
    setSortedToStore () {
      if (this.isEngineer) {
        this.setSortedAttributes({sortedAttributes: {sortingAttr: this.orderbyAttr, reverse: this.reverse}, dashboard: 'tenantProjects'})
      }
    },
  },
}
</script>
<style lang="scss" scoped>
@import "~styles/_settings";
@import "~styles/components/hotb-list";
@import "~styles/dashboard/tenant";
@import "~styles/animation";
.dashboard__table {
  thead th, tbody td {
    padding-left: calc(0.625rem - 1px);
    padding-right: calc(0.625rem - 1px);
    &:first-child {
      padding-left: calc(1.25rem - 1px);
    }
    &:last-child {
      padding-right: calc(1.25rem - 1px);
    }
  }
}
.table-header-title {
  .tradeStatus {
    padding-left: 0;
    padding-right: 0;
    cursor: default;
  }
  .dashboard__table--header th {
    &.projectType {
      width: 150px;
      [lang='zh-CN'] & {
        width: 120px;
      }
    }
    &.batchStatus {
      width: 210px;
      [lang='zh-CN'] & {
        width: 170px;
      }
    }
    &.tradeStatus {
      width: 38px;
    }
    &.floor {
      width: 100px;
    }
    &.status {
      width: 175px;
      span {
        font-weight: bold;
      }
      html[lang='zh-CN'] & {
        width: 135px;
      }
    }
    &.date {
      width: 275px;
      html[lang='zh-CN'] & {
        width: 315px;
      }
    }
  }
}
.table-content {
  td .project-location {
    white-space: pre-line;
  }
}
.table-filter {
  vertical-align: top;
  &-container {
    & > input {
      width: 100%;
    }
    ::v-deep .vdp-datepicker, input {
      min-height: 42px;
    }
  }
}
.hkl-select {
  height: auto;
  min-height: auto;
}
.trade-name {
  &__display {
    display: flex;
    align-items: center;
  }
}

.no-margin-bottom {
  margin-bottom: 0;
}
.new-doc {
  display: inline-block;
  margin-left: 8px;
}

@media screen and (max-width: map-get($breakpoints, "xlarge")) {
  .dashboard__body {
    overflow-x: auto;
    .dashboard__table {
      // 100px = table container padding width + page padding width
      width: map-get($breakpoints, "xlarge") - 100px;
    }
  }
}
</style>
